import { ChangeDetectorRef, Component, Input, ViewContainerRef } from '@angular/core';
import { AngularComponentFactory } from 'survey-angular-ui';
import { AddressFormWidgetModel } from './address-form.widget.model';
import { RendererFactory } from 'survey-core';
import { FormBuilder, Validators } from '@angular/forms';
import { AddressApiService } from '@tangilla/tg-ng-core';
import { AddressFormWidgetJson } from './address-form.widget.json';
import { FormWidgetUtilsService } from '../../../../core/services/utils/form-widget.utils.service';
import { VariablesService } from '../../../../core/services/survey/variables.service';
import { SurveyService } from '../../../../core/services/survey/survey.service';
import { QuestionsService } from '../../../../core/services/survey/questions.service';
import { TangillaQuestion } from '../tangilla-question';
import { ErrorsService } from '../../../../core/services/utils/errors.service';

@Component({
  selector: 'tgf-address-form-widget',
  templateUrl: './address-form.widget.component.html',
  styleUrls: ['./address-form.widget.component.scss']
})
export class AddressFormWidgetComponent extends TangillaQuestion<AddressFormWidgetModel> {
  @Input() requiresValidation: boolean = true;

  isLoading = false;
  isLoadingValidateAddress = false;

  addressValidatedName?: string;

  questionJson: any;

  constructor(
    private addressApiService: AddressApiService,
    public variablesService: VariablesService,
    private formWidgetUtilsService: FormWidgetUtilsService,
    private errorsService: ErrorsService,
    formBuilder: FormBuilder,
    surveyService: SurveyService,
    questionsService: QuestionsService,
    changeDetectorRef: ChangeDetectorRef,
    viewContainerRef?: ViewContainerRef
  ) {
    super(formBuilder, surveyService, questionsService, changeDetectorRef, viewContainerRef);
  }

  override ngOnInit() {
    this.formWidgetJson = new AddressFormWidgetJson(this.questionModel);
    super.ngOnInit();
    this.addressValidatedName = `address_validated_${this.questionName}`;
    this.initForm();
  }

  initForm() {
    this.addAddressFormControls();
    this.questionJson = (this.formWidgetJson as AddressFormWidgetJson).getWidgetJson(this.questionForm!);
    this.questionJson.valueChangesFunctions = {};
    this.questionForm.valueChanges.subscribe(data => {
      if (this.model) {
        this.model.value = undefined;
      }
      this.variablesService.setVariable(this.addressValidatedName!, undefined);
      this.changeDetectorRef.detectChanges();
    });
  }

  addAddressFormControls() {
    this.addFormControl('addressLine1', this.namesReplacements?.addressLine1 || 'ccaddress1', [Validators.required], this.getQuestionValue('addressLine1', 'ccaddress1'));
    this.addFormControl('addressLine2', this.namesReplacements?.addressLine2 || 'ccaddress2', [], this.getQuestionValue('addressLine2', 'ccaddress2'));
    this.addFormControl('state', this.namesReplacements?.state || 'ccstate', [Validators.required], this.getQuestionValue('state', 'ccstate'));
    this.addFormControl('city', this.namesReplacements?.city || 'cccity', [Validators.required], this.getQuestionValue('city', 'cccity'));
    this.addFormControl('zip5', this.namesReplacements?.zip5 || 'cczip', [Validators.required], this.getQuestionValue('zip5', 'cczip'));
  }

  validateAddress() {
    const that = this;
    this.submitForm(() => that.doValidateAddress());
  }

  doValidateAddress() {
    this.isLoadingValidateAddress = true;
    this.model.clearErrors();
    this.model.value = undefined;

    this.changeDetectorRef.detectChanges();
    const params = this.getValidateAddressParams();

    this.addressApiService.validateAddress({params})
      .subscribe({
        next: data => {
          this.processValidateAddress(data);
        },
        error: error => {
          this.processError(error);
        }
      });
  }

  processValidateAddress(data: any) {
    this.mapAddressResponseToForm(data);
    this.variablesService.setVariable(this.addressValidatedName!, true);
    this.isLoadingValidateAddress = false;
    this.model.value = true;
    this.changeDetectorRef.detectChanges();
  }

  processError(error: any) {
    this.model.addError('This address is not valid');
    this.model.value = undefined;
    this.isLoadingValidateAddress = false;
    this.variablesService.setVariable(this.addressValidatedName!, undefined);
    this.errorsService.processError(error, 'soft');
    this.changeDetectorRef.detectChanges();
  }

  mapAddressResponseToForm(data: any) {
    const form = this.questionForm;
    this.formWidgetUtilsService.updateControlValue(form.get('addressLine1'), data.address2);
    this.formWidgetUtilsService.updateControlValue(form.get('addressLine2'), data.address1);
    this.formWidgetUtilsService.updateControlValue(form.get('state'), data.state);
    this.formWidgetUtilsService.updateControlValue(form.get('city'), data.city);
    this.formWidgetUtilsService.updateControlValue(form.get('zip5'), data.zip5);
  }

  getValidateAddressParams() {
    const formValue = this.questionForm.getRawValue();

    return {
      address2: formValue.addressLine1,
      city: formValue.city,
      state: formValue.state,
      zip5: formValue.zip5,
    };
  }

  canShowAddressValid() {
    return this.addressValidatedName &&
      !!this.variablesService.getVariable(this.addressValidatedName);
  }

  isItLoading() {
    return this.isLoading || this.isLoadingValidateAddress;
  }
}

RendererFactory.Instance.registerRenderer(
  'address_form',
  'address_form',
  'tg_address_form',
  true
);
AngularComponentFactory.Instance.registerComponent('tg_address_form', AddressFormWidgetComponent);
